import { useState } from "react";

import { QRCodeSVG } from "qrcode.react";

import { Button, Icon, Loader, Notification } from "@stellar/design-system";

import { CopyWithIcon } from "@/components/CopyWithIcon";
import { DialogModal } from "@/components/DialogModal";

import { truncatePublicKey } from "@/helpers/coSigningView";

import VIAWalletLogoSrc from "@/assets/VIA-wallet-logo.svg";

import "../styles.scss";

export type UnenrollmentSignStage = "pairing" | "scan" | "signing" | "signed";

/** Whose signature this is: the Owner who started the request, or the one responding. */
export type UnenrollmentPerspective = "initiator" | "responder";

type UnenrollmentSignModalProps = {
  visible: boolean;
  stage: UnenrollmentSignStage;
  /** WalletConnect pairing URI, shown as the QR code while `stage` is "scan". */
  uri: string | null;
  signerPublicKey: string | null;
  coSignerCount: number;
  disbursementCount: number;
  error: string | null;
  perspective?: UnenrollmentPerspective;
  /** "Oct 4, 2026 at 2:15 PM": when the request expires, for the initiator's copy. */
  deadline?: string | null;
  onCancel: () => void;
  onDone: () => void;
};

const signingCopy = (perspective: UnenrollmentPerspective, deadline?: string | null) => {
  if (perspective === "responder") {
    return "Scan the code with VIA Wallet to review and sign. Yours is the second and final Owner signature. Co-signing turns off after you sign.";
  }
  const turnsOff = deadline
    ? `Co-signing turns off if the second Owner signs by ${deadline}.`
    : "Co-signing turns off once the second Owner signs.";
  return `Scan the code with VIA Wallet to review and sign. Yours is the first of two Owner signatures. ${turnsOff}`;
};

export const UnenrollmentSignModal = ({
  visible,
  stage,
  uri,
  signerPublicKey,
  coSignerCount,
  disbursementCount,
  error,
  perspective = "initiator",
  deadline,
  onCancel,
  onDone,
}: UnenrollmentSignModalProps) => {
  const [hasCopiedUri, setHasCopiedUri] = useState(false);

  const copyUri = async () => {
    if (!uri) return;
    try {
      await navigator.clipboard.writeText(uri);
      setHasCopiedUri(true);
      setTimeout(() => setHasCopiedUri(false), 1800);
    } catch {
      // Clipboard can be blocked; the QR code still works.
    }
  };

  const renderQrArea = () => {
    if (stage === "signed" && signerPublicKey) {
      return (
        <div className="MultisigManage__signed">
          <span className="MultisigManage__signedCheck">
            <Icon.Check />
          </span>
          <span>
            Signed with <strong>Disbursement signing key</strong>
          </span>
          <CopyWithIcon textToCopy={signerPublicKey} iconSizeRem="0.8">
            <code className="MultisigManage__code" title={signerPublicKey}>
              {truncatePublicKey(signerPublicKey)}
            </code>
          </CopyWithIcon>
        </div>
      );
    }
    if (stage === "signing") {
      return <div className="MultisigManage__pending">Waiting for signature…</div>;
    }
    if (stage === "scan" && uri) {
      return (
        <div className="MultisigManage__qr">
          <QRCodeSVG
            value={uri}
            size={126}
            level="M"
            includeMargin={false}
            imageSettings={{ src: VIAWalletLogoSrc, height: 28, width: 28, excavate: true }}
          />
          <Button variant="tertiary" size="sm" onClick={copyUri}>
            {hasCopiedUri ? "Copied" : "Copy URI"}
          </Button>
        </div>
      );
    }
    return <Loader size="1.5rem" />;
  };

  return (
    <DialogModal
      visible={visible}
      onClose={onCancel}
      title="Sign to turn off co-signing"
      width="60"
      footerAlignment="stretch"
      primaryAction={{ label: "Cancel", variant: "tertiary", onClick: onCancel }}
      secondaryAction={{
        label: "Done",
        variant: "tertiary",
        onClick: onDone,
        disabled: stage !== "signed",
      }}
    >
      <p>{signingCopy(perspective, deadline)}</p>
      {error ? (
        <Notification variant="error" title="Couldn't sign in VIA Wallet" isFilled>
          {error}
        </Notification>
      ) : null}
      <div className="MultisigManage__stage">
        <div className="MultisigManage__stageQr">{renderQrArea()}</div>
        <div className="MultisigManage__stageMeta">
          <div>
            <div className="MultisigManage__label">Action</div>
            <div className="MultisigManage__val">Turn off co-signing</div>
          </div>
          <div>
            <div className="MultisigManage__label">Co-signers to be removed</div>
            <div className="MultisigManage__val">{coSignerCount}</div>
          </div>
          <div>
            <div className="MultisigManage__label">Disbursements to move to drafts</div>
            <div className="MultisigManage__val">{disbursementCount}</div>
          </div>
        </div>
      </div>
    </DialogModal>
  );
};
